/**
 * Catalogue items come in two generations. Picture items store a lowercase identifier in
 * `riveAssetId` (`png` by convention; the first six hold the rig v1 enum names; a name from POSES
 * draws the item in that pose) and ship their PNGs at paths derived from slot + key
 * (docs/addy-rig-contract.md). GIF-era items store an emoji, have no files and are never drawn on Addy.
 */
export const isRigAssetId = (id) => /^[a-z][a-z0-9_]*$/.test(id || '');

export const cosmeticIconUrl = (item) => `/addy/cosmetics/${item.slot}/${item.key}-icon.png`;
export const cosmeticImageUrl = (slot, key, suffix = '') => `/addy/cosmetics/${slot}/${key}${suffix}.png`;

/** The equipped map with `item` tried on in its slot. Shop preview only: nothing is saved. */
export const withPreview = (equipped, item) => (item ? { ...(equipped || {}), [item.slot]: item.key } : equipped || {});

/** Sparks still needed before `item` can be bought; 0 once it is owned or affordable. */
export const sparksToGo = (item, balance) => (item.owned || item.affordable ? 0 : Math.max(0, (item.price || 0) - (Number(balance) || 0)));

/** Rig v8 contract (docs/addy-rig-contract.md). */
export const RIG_URL = '/addy/addy.riv';
export const RIG_STATE_MACHINE = 'State Machine 1';
/**
 * Catalogue slot -> view-model enum property. Since rig v4 the hand enums, the hand image assets
 * and the shop slots all name the hand from Addy's point of view: `handrightEnum` reveals the node
 * that draws `Right Hand Item` (measured 2026-09-29), so each slot maps straight across. Keep this
 * table and RIG_IMAGE_ASSETS paired or a click shows the other hand's picture.
 */
export const SLOT_ENUM = {
  hat: 'capEnum',
  glasses: 'glassesEnum',
  rightHand: 'handrightEnum',
  leftHand: 'handleftEnum',
  shoes: 'shoesEnum',
  trousers: 'trouserEnum',
  back: 'capeEnum',
  skin: 'costumeEnum',
  shirt: 'tshirtEnum',
};
/**
 * Holding a hand item swaps that hand for a gripping hand the rig draws itself in the view-model colour
 * `colorHandFillCostume` (light blue by default), not from the skin's pictures. Each skin sets it to the
 * fill of its own hand pictures (ARGB) so the gripping hand matches; a new skin needs its row here.
 */
export const SKIN_HAND_COLOR_PROP = 'colorHandFillCostume';
export const DEFAULT_SKIN_HAND_COLOR = 0xffd7f1fb; // the rig's own value
export const SKIN_HAND_COLORS = {
  blue_skin: 0xffd8f2fb,
  pink_skin: 0xfffbd3ea,
  mint_green_skin: 0xffb8efd4,
  cherry_blossom_skin: 0xffe1d6f8,
  tiger_stripe_skin: 0xfffdaa41,
  peach_pop_skin: 0xfffec48e,
  gecko_green_skin: 0xffbae363,
  strawberry_cream_skin: 0xfffda0b2,
  // Gradient fills: the average of the hand picture (inside its outline).
  cosmic_galaxy_skin: 0xff4e5fe8,
  prism_crystal_skin: 0xffd9daf8,
  rainbow_cloud_skin: 0xfff1ead1,
  volcanic_lava_skin: 0xffbf6023,
};
export const skinHandColorFor = (equipped) => SKIN_HAND_COLORS[equipped?.skin] ?? DEFAULT_SKIN_HAND_COLOR;
/**
 * Alternative poses: slot enum values that swap the slot to a different pose with its own
 * placeholder. An item whose `riveAssetId` names one is drawn in it; every other picture item
 * uses the slot's plain shown value. Rig v8 added the hands' `book` and `hologram` poses.
 */
export const POSES = ['shield', 'book', 'hologram'];
/**
 * Referenced image asset in the rig -> the catalogue slot it draws, its file suffix and, for a
 * pose's own placeholder, the pose. An asset only receives pictures of items in its pose. A skin
 * is fifteen pictures (body, legs, feet, arm pieces and three hand poses a side), a shirt three.
 */
export const RIG_IMAGE_ASSETS = {
  // Rig v8 draws every hat from this one placeholder, placed per hat by HAT_FIT; its other
  // `Hat Item <name>` assets are unused leftovers from v7.
  'Hat Item santa': { slot: 'hat', suffix: '' },
  'Glasses Item': { slot: 'glasses', suffix: '' },
  'Left Hand Item': { slot: 'leftHand', suffix: '' },
  'Right Hand Item': { slot: 'rightHand', suffix: '' },
  'Left Shield item': { slot: 'leftHand', suffix: '', pose: 'shield' },
  'Right Shield item': { slot: 'rightHand', suffix: '', pose: 'shield' },
  'Book Left item': { slot: 'leftHand', suffix: '', pose: 'book' },
  'Book Right item': { slot: 'rightHand', suffix: '', pose: 'book' },
  // Rig v8 draws the projector in both hands' hologram pose from this one picture, so either hand fills it.
  'hologram projector Right': { slot: 'rightHand', slots: ['rightHand', 'leftHand'], suffix: '', pose: 'hologram' },
  'Trousers Item': { slot: 'trousers', suffix: '' },
  'Left Shoe Item': { slot: 'shoes', suffix: '-left' },
  'Right Shoe Item': { slot: 'shoes', suffix: '-right' },
  Cape: { slot: 'back', suffix: '' },
  'Shirt torso item': { slot: 'shirt', suffix: '' },
  'Sleeve L': { slot: 'shirt', suffix: '-sleeve-l' },
  'Sleeve R': { slot: 'shirt', suffix: '-sleeve-r' },
  'costume body': { slot: 'skin', suffix: '' },
  'Feet costume L': { slot: 'skin', suffix: '-foot-l' },
  'Feet costume R': { slot: 'skin', suffix: '-foot-r' },
  'Leg costume L': { slot: 'skin', suffix: '-leg-l' },
  'Leg costume R': { slot: 'skin', suffix: '-leg-r' },
  'Upper Sleeve L costume': { slot: 'skin', suffix: '-upper-arm-l' },
  'Upper Sleeve R costume': { slot: 'skin', suffix: '-upper-arm-r' },
  'Lower Sleeve L costume': { slot: 'skin', suffix: '-lower-arm-l' },
  'Lower Sleeve R costume': { slot: 'skin', suffix: '-lower-arm-r' },
  'Hand L costume 1': { slot: 'skin', suffix: '-hand-l-1' },
  'Hand L costume 2': { slot: 'skin', suffix: '-hand-l-2' },
  'Hand L costume 3': { slot: 'skin', suffix: '-hand-l-3' },
  'Hand R costume 1': { slot: 'skin', suffix: '-hand-r-1' },
  'Hand R costume 2': { slot: 'skin', suffix: '-hand-r-2' },
  'Hand R costume 3': { slot: 'skin', suffix: '-hand-r-3' },
};
const poseOf = (item) => (item && POSES.includes(item.riveAssetId) ? item.riveAssetId : null);

/**
 * Where each hat sits on Addy's head (rig v8 view-model numbers): scale in percent, X and Y in artboard
 * pixels (negative Y is up), rotation in degrees, all about the hat band. The animator's values, then
 * adjusted by eye on the rig (2026-10-23), for the hat pictures in public/addy/cosmetics/hat. A hat with no
 * row uses HAT_FIT_DEFAULT, the rig's own placement.
 */
export const HAT_FIT_PROPS = { scale: 'hatScale', x: 'hatX', y: 'hatY', rotation: 'hatRotation' };
export const HAT_FIT_DEFAULT = { scale: 100, x: 0, y: 0, rotation: 0 };
export const HAT_FIT = {
  santa_hat: { scale: 100, x: -2.5, y: 0, rotation: 0 },
  lilac_bow_beret: { scale: 140, x: 219.5, y: -34, rotation: 0 },
  daisy_bucket_hat: { scale: 150, x: 346.5, y: -59, rotation: 0 },
  crystal_tiara: { scale: 140, x: 190, y: -63.5, rotation: 0 },
  cowboy_hat: { scale: 150, x: 178.5, y: 42.5, rotation: 0 },
  butterfly_sunhat: { scale: 150, x: 325.5, y: -8.5, rotation: 0 },
  viking_helmet: { scale: 140, x: 158.5, y: -72, rotation: 0 },
  streak_crown: { scale: 130, x: 308.5, y: -122.5, rotation: 0 },
  snapback_hat: { scale: 90, x: 116, y: 12.5, rotation: 0 },
  nova_snapback: { scale: 90, x: 116, y: 38, rotation: 0 },
  moon_stars_witch_hat: { scale: 150, x: 224, y: -59, rotation: 0 },
  champion_headband: { scale: 100, x: 0, y: 177.5, rotation: 0 },
};
export const hatFitFor = (equipped) => HAT_FIT[equipped?.hat] ?? HAT_FIT_DEFAULT;

/**
 * Lesson moods, one view-model boolean each; the page picks one and the rest are switched off.
 * Not shop items: `sway` is the idle move, `talking` plays while Addy's audio plays and `asking`
 * while a question step's audio plays.
 */
export const MOODS = ['sway', 'talking', 'asking'];
export const DEFAULT_MOOD = 'sway';
/**
 * Celebration clips in the rig, one view-model boolean each (`backflip` is a trigger, so it is not
 * playable this way yet). A `celebration` catalogue item names one in `riveAssetId`; unknown or
 * missing falls back to the default cheer.
 */
export const CELEBRATIONS = ['celebrating', 'dancing', 'jumping'];
export const DEFAULT_CELEBRATION = 'celebrating';
// How long one celebration plays (a try-on in the shop, a correct quiz answer): about one play of the clips.
export const CELEBRATION_MS = 2500;
export function celebrationFor(equipped, catalogue) {
  const item = (catalogue || []).find((i) => i.key === equipped?.celebration);
  return item && CELEBRATIONS.includes(item.riveAssetId) ? item.riveAssetId : DEFAULT_CELEBRATION;
}

/** The picture item equipped in each slot, or null when the slot shows nothing (default/legacy). */
function pictureItemsFor(equipped, catalogue) {
  const byKey = new Map((catalogue || []).map((i) => [i.key, i]));
  const out = {};
  for (const slot of Object.keys(SLOT_ENUM)) {
    const item = byKey.get(equipped?.[slot]);
    out[slot] = item && isRigAssetId(item.riveAssetId) ? item : null;
  }
  return out;
}

/**
 * The item key whose picture each rig image asset should hold, or null. A pose's placeholder only
 * takes items in that pose and the plain placeholder only items in none, so a shield never lands
 * in the tall hand placeholder or a sword in the square shield one.
 */
export function assetPicturesFor(equipped, catalogue) {
  const items = pictureItemsFor(equipped, catalogue);
  const out = {};
  for (const [name, { slot, slots = [slot], pose = null }] of Object.entries(RIG_IMAGE_ASSETS)) {
    const item = slots.map((s) => items[s]).find((i) => i && poseOf(i) === pose);
    out[name] = item ? item.key : null;
  }
  return out;
}

/**
 * The enum value each slot should take when a picture item is equipped, otherwise `none`. An item
 * in a pose takes that pose's value; any other takes the first value the loaded rig lists that is
 * neither `none` nor a pose (e.g. `hat`; for the hands `item`), so the animator may rename it.
 * `knownValues` is { enumProperty: [values] } read from the rig, so a catalogue ahead of the
 * deployed rig (a missing slot or pose) degrades to nothing.
 */
export function rigValuesFor(equipped, catalogue, knownValues) {
  const items = pictureItemsFor(equipped, catalogue);
  const out = {};
  for (const [slot, prop] of Object.entries(SLOT_ENUM)) {
    const values = knownValues?.[prop] || [];
    const pose = poseOf(items[slot]);
    const shown = pose ? values.find((v) => v === pose) : values.find((v) => v !== 'none' && !POSES.includes(v));
    out[prop] = items[slot] && shown ? shown : 'none';
  }
  return out;
}
